import { useCallback, useEffect, useMemo, useRef } from 'react';

import { ChakraFlex, ChakraGrid } from '@equip.health/ui';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { ProviderDashboardEvents } from '~/analytics/providerDashboardEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import {
  useGetAttendedPatientsCountQuery,
  useGetUnsignedNotesQuery,
} from '~/store/services/providerDashboard';
import ProviderDashboardGreetingHeader from './ProviderDashboardGreetingHeader';
import ProviderDashboardMyDayCard from './my-day/ProviderDashboardMyDayCard';
import { deriveStatusCounts } from './my-day/statusUtils';
import ProviderDashboardMyPatientsCard from './my-patients/ProviderDashboardMyPatientsCard';
import { useProviderPatientViews } from './my-patients/useProviderPatientViews';
import ProviderDashboardPendingNotesCard from './pending-notes/ProviderDashboardPendingNotesCard';
import './ProviderDashboard.css';

const ProviderDashboard = () => {
  const { currentUser } = useGlobalContext();
  const providerId = currentUser?.userId;

  const {
    data: attendedPatientsCountData,
    isLoading: isLoadingAttendedPatients,
    isFetching: isFetchingAttendedPatients,
    isError: isErrorAttendedPatients,
    refetch: refetchAttendedPatients,
  } = useGetAttendedPatientsCountQuery(
    { providerId: providerId ?? '' },
    { skip: !providerId },
  );

  const {
    data: unsignedNotes,
    isLoading: isLoadingNotes,
    isFetching: isFetchingNotes,
    isError: isErrorNotes,
    refetch: refetchNotes,
  } = useGetUnsignedNotesQuery(
    { providerId: providerId ?? '' },
    { skip: !providerId },
  );

  const {
    patientsByTab,
    refetchAll: refetchAllPatients,
    isRefreshing: isPatientsRefreshing,
  } = useProviderPatientViews(providerId ?? '');

  const todayTab = patientsByTab?.today;
  const appointmentStatusCounts = useMemo(
    () =>
      todayTab?.patients ? deriveStatusCounts(todayTab.patients) : undefined,
    [todayTab?.patients],
  );
  const isLoadingDailySummary = todayTab?.isLoading ?? true;
  const isErrorDailySummary = todayTab?.isError ?? false;

  const { trackEvent } = useAnalytics() ?? {};

  const hasTrackedView = useRef(false);
  useEffect(() => {
    if (!hasTrackedView.current) {
      hasTrackedView.current = true;
      trackEvent?.(ProviderDashboardEvents.viewed, {
        Source: AnalyticsSourceValues.PROVIDER_DASHBOARD,
      });
    }
  }, [trackEvent]);

  const handleRefresh = useCallback(() => {
    trackEvent?.(ProviderDashboardEvents.refreshed, {
      Source: AnalyticsSourceValues.PROVIDER_DASHBOARD,
    });
    refetchAttendedPatients();
    refetchNotes();
    refetchAllPatients();
  }, [trackEvent, refetchAttendedPatients, refetchNotes, refetchAllPatients]);

  const isAnyFetching =
    isFetchingAttendedPatients || isFetchingNotes || isPatientsRefreshing;

  const displayName =
    currentUser?.displayFirstName || currentUser?.firstName || '';
  return (
    <ChakraFlex
      data-testid="provider-dashboard"
      direction="column"
      height="100%"
      overflowY="auto"
      width="100%"
    >
      <ChakraFlex
        direction="column"
        gap="28px"
        padding="28px 40px"
        maxWidth="1320px"
        width="100%"
      >
        <ProviderDashboardGreetingHeader
          displayName={displayName}
          distinctPatientsCount={attendedPatientsCountData?.count}
          isLoadingPatientsCount={
            isLoadingAttendedPatients || isFetchingAttendedPatients
          }
          isErrorPatientsCount={isErrorAttendedPatients}
          onRefresh={handleRefresh}
          isRefreshing={isAnyFetching}
        />

        <ChakraGrid templateColumns="2fr 1fr" gap="28px" alignItems="start">
          <ChakraFlex direction="column" gap="28px" minWidth="0">
            <ProviderDashboardMyDayCard
              appointments={appointmentStatusCounts}
              isLoading={isLoadingDailySummary}
              isError={isErrorDailySummary}
              onReload={handleRefresh}
              isReloading={isAnyFetching}
            />
            <ProviderDashboardMyPatientsCard
              patientsByTab={patientsByTab}
              onReload={handleRefresh}
              isReloading={isAnyFetching}
            />
          </ChakraFlex>

          <ChakraFlex direction="column" gap="28px" minWidth="0">
            <ProviderDashboardPendingNotesCard
              notes={unsignedNotes}
              isLoading={isLoadingNotes || isFetchingNotes}
              isError={isErrorNotes}
              onReload={handleRefresh}
              isReloading={isAnyFetching}
            />
          </ChakraFlex>
        </ChakraGrid>
      </ChakraFlex>
    </ChakraFlex>
  );
};

export default ProviderDashboard;
